/**
 * Shared Authentication Token Manager
 *
 * Manages a single shared auth token across the application.
 * Validates tokens against the Hermes server's ping endpoint.
 */

import { config } from "../config/app-config";

const AUTH_TOKEN_KEY = "qubes_auth_token";
// Per-TAB override (sessionStorage, not shared across tabs). When set, getToken()
// returns this instead of the shared localStorage token. Impersonation uses it so
// "act as another user" takes over ONLY the current tab — other tabs keep reading
// the shared localStorage token (still the original/admin identity).
const AUTH_TOKEN_OVERRIDE_KEY = "qubes_auth_token_override";

export interface TokenValidationResponse {
  loggedIn: boolean;
}

// Subscribers notified when a FRESH token is stored — i.e. the user logged back
// in. The hermes client subscribes to release its auth halt and dial once;
// nothing else can tell it a token exists, because a halted client is by
// definition not talking to the server. A callback list rather than a direct
// import: this module must not depend on the client, which already imports it.
// See BUG-20260910-ws-onclose-redials-through-unauthenticated.
type TokenSetListener = () => void;
const tokenSetListeners: TokenSetListener[] = [];

export function onAuthTokenSet(listener: TokenSetListener): void {
  tokenSetListeners.push(listener);
}

export class AuthTokenManager {
  private static instance: AuthTokenManager;
  private token: string | undefined = undefined;
  private qubesUrl: string;
  private hermesUrl: string;

  private constructor() {
    this.qubesUrl = config.qubes?.publicUrl ?? "";
    this.hermesUrl = config.hermes.url;
    // Prefer a per-tab override (impersonation) over the shared localStorage
    // token, so this tab can act as a different identity without affecting others.
    const override = sessionStorage.getItem(AUTH_TOKEN_OVERRIDE_KEY);
    this.token = override
      ? override
      : localStorage.getItem(AUTH_TOKEN_KEY)
        ? localStorage.getItem(AUTH_TOKEN_KEY)!
        : undefined;
    console.log("[AuthTokenManager] Initialized");
    console.log("[AuthTokenManager] Qubes URL:", this.qubesUrl);
    console.log("[AuthTokenManager] Hermes URL:", this.hermesUrl);
    console.log(
      "[AuthTokenManager] Loaded token from localStorage:",
      this.token ? "YES" : "NO"
    );
  }

  /**
   * Get the singleton instance of AuthTokenManager. The init() method
   * must be called first to initialize the instance.
   * @returns AuthTokenManager instance
   */
  public static getInstance(): AuthTokenManager {
    if (!AuthTokenManager.instance) {
      throw new Error(
        "AuthTokenManager not initialized. Call AuthTokenManager.init() first."
      );
    }
    return AuthTokenManager.instance;
  }

  /**
   * Initialize the AuthTokenManager singleton. This must be called once before
   * getInstance() is used. This is called from the application entry point (index.tsx).
   * Configuration is loaded from the unified app-config.
   */
  public static init(): void {
    AuthTokenManager.instance = new AuthTokenManager();
  }

  /**
   * Get the current auth token (may be invalid). A per-tab impersonation
   * override (sessionStorage) takes precedence over the shared localStorage
   * token, so consumers automatically act as the impersonated identity in THIS
   * tab only.
   */
  public getToken(): string | undefined {
    const override = sessionStorage.getItem(AUTH_TOKEN_OVERRIDE_KEY);
    if (override) return override;
    return this.token;
  }

  /**
   * Set a per-TAB token override (impersonation). Written to sessionStorage so
   * it does NOT affect other tabs (which keep using the shared localStorage
   * token). The shared token is left untouched, so returning to the original
   * identity is just clearSessionOverride().
   */
  public setSessionOverride(token: string): void {
    sessionStorage.setItem(AUTH_TOKEN_OVERRIDE_KEY, token);
  }

  /** Remove the per-tab override; getToken() falls back to the shared token. */
  public clearSessionOverride(): void {
    sessionStorage.removeItem(AUTH_TOKEN_OVERRIDE_KEY);
  }

  /** True if this tab currently has an impersonation override active. */
  public hasSessionOverride(): boolean {
    return !!sessionStorage.getItem(AUTH_TOKEN_OVERRIDE_KEY);
  }

  /**
   * Set the auth token and persist to localStorage
   */
  public setToken(token: string): void {
    this.token = token;
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    console.log("[AuthTokenManager] Token saved to localStorage");
    // A fresh token is the ONLY thing that can release an auth-halted client, so
    // announce it. Listener errors are contained: one bad subscriber must not
    // stop the token from being stored, nor the others from hearing about it.
    for (const listener of tokenSetListeners) {
      try {
        listener();
      } catch (e) {
        console.warn("[AuthTokenManager] token-set listener threw", e);
      }
    }
  }

  /**
   * Clear the auth token
   */
  public clearToken(): void {
    this.token = undefined;
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }

  /**
   * Validate the current token against the Hermes server
   * Returns true if token is valid (user is logged in)
   */
  public async validateToken(): Promise<boolean> {
    if (!this.token) {
      console.log("[AuthTokenManager] No token to validate");
      return false;
    }

    console.log(
      "[AuthTokenManager] Validating token against:",
      `${this.hermesUrl}/api/ping`
    );

    try {
      // hermes /api/ping — authed via the canonical X-Auth-Token header
      // (X-SESS was the legacy Qubes-shaped header; removed).
      const response = await fetch(`${this.hermesUrl}/api/ping`, {
        method: "GET",
        headers: {
          "X-Auth-Token": this.token,
        },
        credentials: "include",
      });

      console.log(
        "[AuthTokenManager] Validation response status:",
        response.status
      );

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // The server JUDGED the token and rejected it - clear it
          console.log("[AuthTokenManager] Token rejected - clearing token");
          this.clearToken();
          return false;
        }
        // Any other status (5xx from a proxy whose upstream is down, etc.) is
        // the backend being unreachable, NOT an auth verdict — keep the token.
        // Clearing here let a 2s gateway blip destroy a valid 24h session
        // (BUG-20260802-react-transient-outage-hard-navigates-to-login).
        console.log(
          `[AuthTokenManager] Validation got HTTP ${response.status} - no verdict, keeping token`
        );
        return false;
      }

      const data: TokenValidationResponse = await response.json();
      console.log("[AuthTokenManager] Validation response:", data);

      if (!data.loggedIn) {
        // Token is invalid - clear it
        console.log("[AuthTokenManager] Token not logged in - clearing token");
        this.clearToken();
        return false;
      }

      console.log("[AuthTokenManager] Token is valid!");
      return true;
    } catch (error) {
      console.error("[AuthTokenManager] Failed to validate token:", error);
      // Network error - keep token but report as invalid for now
      return false;
    }
  }

  /**
   * Get a valid token, checking existing token first
   * Returns null if no valid token exists
   */
  public async getValidToken(): Promise<string | undefined> {
    const isValid = await this.validateToken();
    return isValid ? this.token : undefined;
  }

  /**
   * Check if we have a token (doesn't validate it)
   */
  public hasToken(): boolean {
    return this.token !== null;
  }
}
